import FirebaseAdapter from "@wc/adapters/firebase";
import {
  userHasPermissionInTeam,
  PermissionsEnum,
  roleToNumeric,
  numericToRole,
} from "@wc/domain/authz";

import { TeamRoles } from "../constants";

/** @typedef { import("@wc/domain/users").AuthnUser } AuthnUser */

/**
 * @typedef {object} TeamListUser
 * @property {string} id The user id
 * @property {string} displayName The user's display name
 * @property {string} [photoURL] The user's photo URL
 * @property {string} role The user's role in the team
 */

/**
 * @param {string} teamId The id of the team to check
 * @param {string} _accessUserId The user requesting access (validated server-side)
 * @returns {Promise<TeamListUser[]>} The users in the team
 */
export async function listTeamMembers(teamId, _accessUserId) {
  const result = await FirebaseAdapter().functions.getTeamMembers({ teamId });

  if (!result.success) {
    throw new Error(`Unable to access team ${teamId}`);
  }

  return result.members.map((m) => ({
    id: m.id,
    displayName: m.displayName,
    photoURL: m.photoURL,
    createdAt: m.createdAt ? new Date(m.createdAt) : null,
    role: m.role ? numericToRole(m.role) : null,
  }));
}

/**
 * @param {string} teamId The team to look at
 * @param {string} userId The user to remove
 * @param {AuthnUser} accessUser A full authenticated user to check
 */
export async function removeTeamMember(teamId, userId, accessUser) {
  if (userId === accessUser.id) {
    throw new Error("You cannot delete yourself");
  }

  const hasRemovePermission = await userHasPermissionInTeam(
    accessUser,
    teamId,
    PermissionsEnum.REMOVE_TEAM_MEMBER,
  );

  if (!hasRemovePermission) {
    throw new Error("You do not have permission to remove this user");
  }

  // Call the removeFromTeam function
  await FirebaseAdapter().functions.removeFromTeam({
    teamId,
    targetUserId: userId,
  });
}

/**
 * @param {string} teamId The team to look at
 * @param {string} userId The user to operate on
 * @param {string} newRole The new role to assign the user
 * @param {AuthnUser} accessUser A full authenticated user to check
 */
export async function updateTeamMemberRole(
  teamId,
  userId,
  newRole,
  accessUser,
) {
  if (userId === accessUser.id) {
    throw new Error("You cannot modify your own role");
  }

  if (!TeamRoles.includes(newRole)) {
    throw new Error(`invalid role assignment for team ${teamId}: ${newRole}`);
  }

  const hasChangePermission = await userHasPermissionInTeam(
    accessUser,
    teamId,
    PermissionsEnum.MANAGE_TEAM_ROLES,
  );

  if (!hasChangePermission) {
    throw new Error("You do not have permission to alter this user's role");
  }

  // Call the updateTeamRole function
  await FirebaseAdapter().functions.updateTeamRole({
    teamId,
    targetUserId: userId,
    newRole: roleToNumeric(newRole),
  });

  // No need for token refresh - the function handles it
}

/**
 * Adds an existing org member to a team
 * @param {string} teamId The team to add to
 * @param {string} userId The user to add (must be org member)
 * @param {AuthnUser} accessUser The user performing the action
 * @returns {Promise<void>}
 */
export async function addTeamMember(teamId, userId, accessUser) {
  const hasPermission = await userHasPermissionInTeam(
    accessUser,
    teamId,
    PermissionsEnum.MANAGE_TEAM_MEMBERS,
  );

  if (!hasPermission) {
    throw new Error("You do not have permission to add team members");
  }

  // Call the addToTeam function
  await FirebaseAdapter().functions.addToTeam({
    teamId,
    targetUserId: userId,
  });
}
